import {
  Content,
  ContentNodeType,
  InlineComment,
  InlineEditItem,
} from '@amzn/awscat-pattern-library-service-model';
import { log } from '../loggers/Logger';
import { listEpics } from './EpicOperations';
import { SpecialSectionId } from './SectionOperations';
import { listTroubleshootings } from './TroubleshootingOperations';
import { RichtextDocId } from '../model/RichtextDocId';

interface ProseMirrorNode {
  readonly attrs?: { readonly nodeId?: string };
  readonly content?: readonly ProseMirrorNode[];
  readonly text?: string;
}

interface CollectedMappings {
  readonly subContents: Record<string, NodeIdSet>;
  readonly contentSections: Record<string, NodeIdSet>;
}

function isProseMirrorNode(value: unknown): value is ProseMirrorNode {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

const NODE_ID_POSITIONS = new WeakMap<NodeIdSet, ReadonlyMap<string, number>>();

/** Recursively collects all `nodeId` attributes from a ProseMirror document tree. */
function collectNodeIds(doc: unknown): NodeIdSet {
  const ids: NodeIdSet = new Set();
  const positions = new Map<string, number>();

  // Return the ProseMirror size of the traversed subtree so positions accumulate
  // in a single pass — text is its length, a container is 2 + its content, atoms are 1.
  function walk(node: unknown, position: number, isDocumentRoot: boolean): number {
    if (!isProseMirrorNode(node)) return 1;

    const nodeId = node.attrs?.nodeId;
    if (typeof nodeId === 'string' && nodeId.length > 0) {
      ids.add(nodeId);
      positions.set(nodeId, position + 1);
    }

    if (typeof node.text === 'string') return node.text.length;

    if (Array.isArray(node.content)) {
      let childPosition = isDocumentRoot ? 0 : position + 1;
      let contentSize = 0;
      for (const child of node.content) {
        const childSize = walk(child, childPosition, false);
        childPosition += childSize;
        contentSize += childSize;
      }
      return 2 + contentSize;
    }

    return 1;
  }

  walk(doc, 0, true);
  NODE_ID_POSITIONS.set(ids, positions);
  return ids;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function hasStringId(
  record: Record<string, unknown>,
): record is Record<string, unknown> & { id: string } {
  return typeof record.id === 'string' && record.id.length > 0;
}

const IGNORED_SECTION_IDS: ReadonlySet<string> = new Set([
  SpecialSectionId.Troubleshooting,
  SpecialSectionId.Epics,
]);

function collectMappingsRecursively(
  container: Record<string, unknown>,
  result: { subContents: Record<string, NodeIdSet>; contentSections: Record<string, NodeIdSet> },
): void {
  const subContents = container.subContent;
  if (Array.isArray(subContents)) {
    for (const item of subContents) {
      if (!isRecord(item) || !hasStringId(item)) continue;

      result.subContents[item.id] = collectNodeIds(item.contentData);
      collectMappingsRecursively(item, result);
    }
  }

  const contentSections = container.contentSections;
  if (Array.isArray(contentSections)) {
    for (const item of contentSections) {
      if (!isRecord(item) || !hasStringId(item)) continue;
      if (IGNORED_SECTION_IDS.has(item.id)) continue;

      result.contentSections[item.id] = collectNodeIds(item.contentData);
      collectMappingsRecursively(item, result);
    }
  }
}

function buildSubContentAndSectionMappings(content: Content): CollectedMappings {
  const result: {
    subContents: Record<string, NodeIdSet>;
    contentSections: Record<string, NodeIdSet>;
  } = {
    subContents: {},
    contentSections: {},
  };

  const contentData: unknown = content.contentData;
  if (isRecord(contentData)) {
    collectMappingsRecursively(contentData, result);
  }

  return result;
}

function buildEpicMappings(content: Content): Record<string, EpicNodeIdMapping> {
  let epics;
  try {
    epics = listEpics(content);
  } catch (e) {
    log.error(e, '[InlineTextOperations] corrupted epic data');
    return {};
  }
  if (epics.length === 0) return {};

  const result: Record<string, EpicNodeIdMapping> = {};

  for (const epic of epics) {
    // Collect node ids from the Epic title and description
    result[epic.id] = {
      title: collectNodeIds(epic.title),
      description: collectNodeIds(epic.description),
    };

    // Collect node ids from each Epic's task title and description
    for (const task of epic.tasks) {
      result[task.id] = {
        title: collectNodeIds(task.title),
        description: collectNodeIds(task.description),
      };
    }
  }

  return result;
}

function buildRootMapping(content: Content): NodeIdSet {
  const contentData: unknown = content.contentData;
  if (!isRecord(contentData)) return new Set();

  return collectNodeIds(contentData.contentData);
}

function buildTroubleshootingMappings(
  content: Content,
): Record<string, TroubleshootingNodeIdMapping> {
  let troubleshootings;
  try {
    troubleshootings = listTroubleshootings(content);
  } catch (e) {
    log.error(e, '[InlineTextOperations] corrupted troubleshooting data');
    return {};
  }
  if (troubleshootings.length === 0) return {};

  const result: Record<string, TroubleshootingNodeIdMapping> = {};

  for (const item of troubleshootings) {
    result[item.id] = {
      issue: collectNodeIds(item.issue),
      solution: collectNodeIds(item.solution),
    };
  }

  return result;
}

const EMPTY_SET: Set<string> = Object.freeze(new Set<string>());

function resolveNodeIds(filter: InlineTextFilter, mapping: NodeIdMappings): NodeIdSet | undefined {
  switch (filter.type) {
    case 'none':
      return EMPTY_SET;
    case 'section':
      if (filter.nodeType === ContentNodeType.Document) return mapping.root;
      if (filter.nodeType === ContentNodeType.Page) return mapping.subContents[filter.sectionId];
      return mapping.contentSections[filter.sectionId];
    case 'epic_title':
      return mapping.epics[filter.epicId]?.title;
    case 'epic_description':
      return mapping.epics[filter.epicId]?.description;
    case 'epic_task_title':
      return mapping.epics[filter.taskId]?.title;
    case 'epic_task_description':
      return mapping.epics[filter.taskId]?.description;
    case 'troubleshooting_issue':
      return mapping.troubleshootings[filter.troubleshootingId]?.issue;
    case 'troubleshooting_solution':
      return mapping.troubleshootings[filter.troubleshootingId]?.solution;
  }
}

export type NodeIdSet = Set<string>;

export interface EpicNodeIdMapping {
  readonly title: NodeIdSet;
  readonly description: NodeIdSet;
}

export interface TroubleshootingNodeIdMapping {
  readonly issue: NodeIdSet;
  readonly solution: NodeIdSet;
}

export interface NodeIdMappings {
  readonly root: NodeIdSet;
  /** Keys are epic IDs or epic task IDs; values hold node IDs collected from their title and description. */
  readonly epics: Record<string, EpicNodeIdMapping>;
  readonly subContents: Record<string, NodeIdSet>;
  readonly contentSections: Record<string, NodeIdSet>;
  readonly troubleshootings: Record<string, TroubleshootingNodeIdMapping>;
}

/**
 * Collects all ProseMirror node IDs across a content's epics, subContents,
 * and contentSections. Returns a mapping keyed by section/epic ID to the
 * set of node IDs each contains. Used to correlate inline edits and comments
 * back to their originating ProseMirror document.
 */
export function getNodeIdMappings(content: Content): NodeIdMappings {
  const epics = buildEpicMappings(content);
  const { subContents, contentSections } = buildSubContentAndSectionMappings(content);
  const root = buildRootMapping(content);
  const troubleshootings = buildTroubleshootingMappings(content);

  return { root, epics, subContents, contentSections, troubleshootings };
}

/** Selects which richtext document's inline edits to display; `none` means display nothing. */
export type InlineTextFilter = RichtextDocId | { readonly type: 'none' };

/** Filters inline edit items to only those whose nodeId belongs to the specified content region. */
export function filterInlineEdits(
  filter: InlineTextFilter,
  mapping: NodeIdMappings,
  items: readonly InlineEditItem[],
): readonly InlineEditItem[] {
  const nodeIds = resolveNodeIds(filter, mapping);
  if (nodeIds === undefined) return [];

  return items.filter((item) => nodeIds.has(item.nodeId));
}

/** Checks whether an inline comment path belongs to the given richtext document. */
export function commentPathMatchesDocId(
  path: readonly string[],
  id: RichtextDocId,
  nodeIdMappings: NodeIdMappings,
): boolean {
  // AI/scanner comments store a nodeId as path[0]. Check if it belongs to
  // the resolved set of node IDs for this content region.
  const firstSegment = path[0];
  if (firstSegment) {
    const nodeIds = resolveNodeIds(id, nodeIdMappings);
    if (nodeIds && nodeIds.has(firstSegment)) return true;
  }

  // Legacy path-based matching — kept for backward compatibility with the legacy UI.
  // Will be removed once the legacy UI is fully retired.
  switch (id.type) {
    case 'section':
      if (path[0] === 'epics' || path[0] === 'troubleshooting') return false;
      // Pattern: path = ['sectionId']. Guide/Strategy: path = ['pageTitle', 'sectionNodeId'].
      return path[0] === id.sectionId || path[1] === id.sectionId;
    case 'epic_title':
      return path[0] === 'epics' && path[1] === `title-${id.epicId}`;
    case 'epic_description':
      return path[0] === 'epics' && path[1] === `desc-${id.epicId}`;
    case 'epic_task_title':
      return path[0] === 'epics' && path[1] === `${id.taskId}-story-title`;
    case 'epic_task_description':
      return path[0] === 'epics' && path[1] === id.taskId;
    case 'troubleshooting_issue':
      return (
        path[0] === 'troubleshooting' && path[1] === 'issue' && path[2] === id.troubleshootingId
      );
    case 'troubleshooting_solution':
      return (
        path[0] === 'troubleshooting' && path[1] === 'solution' && path[2] === id.troubleshootingId
      );
  }
}

/**
 * Returns a ProseMirror node's absolute start position within a richtext document.
 */
export function getNodeIdPosition(
  nodeId: string | undefined,
  id: RichtextDocId,
  nodeIdMappings: NodeIdMappings,
): number | undefined {
  if (!nodeId) return undefined;

  const nodeIds = resolveNodeIds(id, nodeIdMappings);
  if (!nodeIds) return undefined;
  return NODE_ID_POSITIONS.get(nodeIds)?.get(nodeId);
}

/** Produces the comment path array for creating a new inline comment. */
export function docIdToCommentPath(id: RichtextDocId): string[] {
  // Path format matches what the legacy UI produces — see AuthorContentViewer read/edit mode.
  // Once the legacy UI retires, we will switch to use node ids as path, as
  // being used in commentPathMatchesDocId()
  switch (id.type) {
    case 'section':
      return [id.sectionId];
    case 'epic_title':
      return ['epics', `title-${id.epicId}`];
    case 'epic_description':
      return ['epics', `desc-${id.epicId}`];
    case 'epic_task_title':
      return ['epics', `${id.taskId}-story-title`];
    case 'epic_task_description':
      return ['epics', id.taskId];
    case 'troubleshooting_issue':
      return ['troubleshooting', 'issue', id.troubleshootingId];
    case 'troubleshooting_solution':
      return ['troubleshooting', 'solution', id.troubleshootingId];
  }
}

/** Filters inline comments to only those whose path belongs to the specified content region. */
export function filterInlineComments(
  filter: InlineTextFilter,
  nodeIdMappings: NodeIdMappings,
  comments: readonly InlineComment[],
): readonly InlineComment[] {
  if (filter.type === 'none') return [];
  return comments.filter((c) => commentPathMatchesDocId(c.path, filter, nodeIdMappings));
}
